import {
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    ShoppingCart as CartIcon,
    Delete as DeleteIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import { useCart } from '../context/CartContext';
import { useConfirmation } from '../hooks/useConfirmation';
import { confirmations } from '../utils/confirmation';

const Cart: React.FC = () => {
  const { state, removeItem, updateQuantity, clearCart } = useCart();
  const { confirmation, showConfirmation } = useConfirmation();

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString() + ' FCFA';
  };

  // Handle quantity change
  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity >= 1) {
      updateQuantity(id, newQuantity);
    }
  };

  // Handle remove item
  const handleRemoveItem = (itemId: string, itemName: string) => {
    showConfirmation(
      confirmations.delete(itemName),
      () => removeItem(itemId)
    );
  };

  // Handle clear cart
  const handleClearCart = () => {
    showConfirmation(
      confirmations.clear('le panier'),
      () => clearCart()
    );
  };

  // Handle checkout
  const handleCheckout = () => {
    showConfirmation(confirmations.checkout(), () => {
      // Navigate to new checkout page
      window.location.href = '/checkout';
    });
  };

  if (state.items.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            Votre panier est vide
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Découvrez nos outils, équipements et fournitures et commencez vos achats
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={RouterLink}
            to="/products"
            startIcon={<ArrowBackIcon />}
          >
            Continuer les achats
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Votre Panier
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {state.itemCount} article(s) dans votre panier
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: { xs: 2, md: 3 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h6">
                Articles ({state.items.length})
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClearCart}
              >
                Vider le panier
              </Button>
            </Box>

            <Stack spacing={3}>
              {state.items.map((item) => (
                <Card key={item.id} sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  overflow: 'hidden'
                }}>
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: { xs: '100%', sm: 120 }, 
                      height: { xs: 200, sm: 120 }, 
                      objectFit: 'cover' 
                    }}
                    image={item.image}
                    alt={item.name}
                  />
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography variant="h6" gutterBottom sx={{ 
                          fontSize: { xs: '1.1rem', sm: '1.25rem' },
                          lineHeight: 1.2
                        }}>
                          {item.name}
                        </Typography>
                        <Typography variant="h6" color="primary" gutterBottom sx={{
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                          {formatPrice(item.price)}
                        </Typography>
                        {item.stock < 50 && (
                          <Chip 
                            label="Stock limité" 
                            color="warning" 
                            size="small" 
                            sx={{ mb: 1 }}
                          />
                        )}
                      </Box>
                      <IconButton
                        color="error"
                        onClick={() => handleRemoveItem(item.id, item.name)}
                        size="small"
                        sx={{ flexShrink: 0 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto', flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        Quantité:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <TextField
                          value={item.quantity}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            if (!isNaN(value)) {
                              handleQuantityChange(item.id, value);
                            }
                          }}
                          inputProps={{
                            min: 1,
                            max: item.stock,
                            style: { textAlign: 'center', width: 60 }
                          }}
                          size="small"
                          sx={{ '& .MuiOutlinedInput-root': { border: 'none' } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={item.quantity >= item.stock}
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Stock: {item.stock}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Sous-total: {formatPrice(item.price * item.quantity)}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ 
            p: { xs: 2, md: 3 }, 
            position: { lg: 'sticky' }, 
            top: { lg: 20 },
            height: 'fit-content'
          }}>
            <Typography variant="h6" gutterBottom>
              Résumé de la commande
            </Typography>

            <Stack spacing={2} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Sous-total ({state.itemCount} articles)</Typography>
                <Typography>{formatPrice(state.total)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Livraison</Typography>
                <Typography color="success.main">Gratuite</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total</Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(state.total)}
                </Typography>
              </Box>
            </Stack>

            <Alert severity="info" sx={{ mb: 3 }}>
              Livraison gratuite pour toute commande supérieure à 50 000 FCFA
            </Alert>

            <Stack spacing={2}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleCheckout}
                disabled={state.items.length === 0}
                startIcon={<CartIcon />}
              >
                Procéder au paiement
              </Button>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                component={RouterLink}
                to="/products"
                startIcon={<ArrowBackIcon />}
              >
                Continuer les achats
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Modal */}
      {confirmation && (
        <ConfirmationModal
          open={confirmation.open}
          title={confirmation.title}
          message={confirmation.message}
          type={confirmation.type}
          confirmText={confirmation.confirmText}
          cancelText={confirmation.cancelText}
          onConfirm={confirmation.onConfirm}
          onCancel={confirmation.onCancel}
          confirmButtonColor={confirmation.confirmButtonColor}
        />
      )}
    </Container>
  );
};

export default Cart; 